import dayjs, { Dayjs } from 'dayjs';
import * as advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { combinedIcons, determineTimeUnit, getParameterValues, getTimeZone, instanaTechnology } from "./utils";
import { API_DATE_FORMAT } from './timeRangeUtils';
import { TimeRange } from "../types/TimeRange";

// Api.
import api from '../api/Base';
import axios from 'axios';
import { showError } from '../utils/showAlert';

// Constants.
import {
    CHART_DATA_TYPE,
    HOST_TYPE_AWSRDS,
    HOST_TYPE_LINUX,
    HOST_TYPE_WINDOWS,
    INSTANCE_TYPE_CONFIG,
    INTERVAL_10_SECONDS,
    INTERVAL_15_SECONDS, INTERVAL_5_SECONDS,
    INTERVAL_FIFTEEN_MINUTES,
    INTERVAL_FIVE_MINUTES,
    INTERVAL_FOUR_HOURS,
    INTERVAL_MINUTE,
    INTERVAL_ONE_DAY,
    INTERVAL_ONE_HOUR,
    INTERVAL_ONE_WEEK,
    INTERVAL_SECOND,
    INTERVAL_SIX_HOURS,
    INTERVAL_TEN_MINUTES,
    INTERVAL_THIRTY_MINUTES,
    INTERVAL_TWELVE_HOURS,
    periodToValidIntervals,
    TARGET_COLOUR_CODES
} from '../component/Constants';
import { TIME_METRICS } from "../container/comparison/statistics-comparison/constants";

// Types.
import InstanceTarget from '../types/instance/InstanceTarget';
import HostTarget from '../types/host/HostTarget';
import ChartPlotLine from '../types/ChartPlotLine';
// import ChartPlotBand from '../types/ChartPlotBand';
import CombinedChanges from '../types/instance/CombinedChanges';
import Database from '../types/instance/Database';
import InstanaIntegration from '../types/integration/InstanaIntegration';
import InstanaService from '../types/integration/InstanaService';
import ExecutionPlan from '../types/instance/ExecutionPlan';
import EventType from '../types/instance/EventType';
import FilterOption from '../types/instance/FilterOption';
import { FilterType } from '../typescript/Enums';
import { InstanceOptions } from "../container/comparison/statistics-comparison/types";

// DayJS plugins - note, the position of these in this file are important.
dayjs.extend(advancedFormat.default);
dayjs.extend(utc);
dayjs.extend(timezone);

const Helper = {
    getFilterParams: () => {
        return [{
            filterType: FilterType.Waits,
            filters: getParameterValues(window.location.search, 'waits')
        }, {
            filterType: FilterType.Databases,
            filters: getParameterValues(window.location.search, 'databases')
        }, {
            filterType: FilterType.Sessions,
            filters: getParameterValues(window.location.search, 'sessions')
        }, {
            filterType: FilterType.Clients,
            filters: getParameterValues(window.location.search, 'clients')
        }, {
            filterType: FilterType.Users,
            filters: getParameterValues(window.location.search, 'users')
        }, {
            filterType: FilterType.Programs,
            filters: getParameterValues(window.location.search, 'programs')
        }]
    },

    getChartTooltipsNew: (tooltip: any, chartDataType: number) => {

        let data: string = '';

        for (let index = 0; index < tooltip.points.length; index++) {

            const point: any = tooltip.points[index];

            if (point.series.name === 'Executions') {
                data = data + `<div class="d-flex justify-content-between"><div><div class="dot" style="background-color: ${point.color}"></div>${point.series.name}:</div> <span style="padding-left: 20px" class="value">${Helper.getFormattedNumber(point.y)}</span></div>`
            } else {

                if (point.y > 0) {

                    switch (chartDataType) {
                        case CHART_DATA_TYPE.GENERIC:

                            data = data + `<div class="d-flex justify-content-between"><div><div class="dot" style="background-color: ${point.color}"></div>${point.series.name}:</div> <span style="padding-left: 20px" class="value">${Helper.getFormattedNumber(point.y)}</span></div>`

                            break;

                        case CHART_DATA_TYPE.PERCENTAGE:

                            data = data + `<div class="d-flex justify-content-between"><div><div class="dot" style="background-color: ${point.color}"></div>${point.series.name}:</div> <span style="padding-left: 20px" class="value">${point.y.toFixed(2)}%</span></div>`

                            break;

                        case CHART_DATA_TYPE.TIME:

                            data = data + `<div class="d-flex justify-content-between"><div><div class="dot" style="background-color: ${point.color}"></div>${point.series.name}:</div> <span style="padding-left: 20px" class="value">${Helper.getTimeInEnglish(point.y)}</span></div>`

                            break;

                        default:

                            console.error('Unknown chart data type requested for custom tooltip formatter.', chartDataType);

                            break;
                    }
                }
            }
        }
        return `<div class="chart-tooltip-new"><div class="tooltip-header">${Helper.formatDate(tooltip.x).replace(" at", ",")}</div>${data}`;
    },

    formatDate: (date: number | Date) => {
        const ts = typeof date === 'number' ? date : date.getTime();
        if (!Number.isFinite(ts)) {
            return '';
        }
        const tz = getTimeZone();
        const options: Intl.DateTimeFormatOptions = {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: false,
            timeZone: tz,
        };

        const formatter = new Intl.DateTimeFormat('en-US', options);
        return formatter.format(ts);
    },

    getChartAxisCategories: (from: Dayjs, to: Dayjs, interval: number) => {
        const { dateFormat } = Helper.getIntervalHandling(interval);
        const categories = [];

        // const effectiveIncrement = incrementUnit || interval;
        const effectiveIncrement = interval;

        const fromTimestamp = from.valueOf();
        const alignedTimestamp = fromTimestamp - (fromTimestamp % effectiveIncrement);

        // Ensure consistent time handling
        let current = from.isUTC() ? dayjs.utc(alignedTimestamp) : dayjs(alignedTimestamp);

        while (current.isBefore(to) || current.isSame(to)) {
            categories.push(current.format(dateFormat));
            current = current.add(effectiveIncrement, 'ms');
        }

        return categories;
    },

    getInterval: (from: Dayjs, to: Dayjs, interval?: number) => {

        // If interval is already set, return it
        if (interval) {
            return interval
        }

        // If interval is auto calculate an interval
        const time = to.diff(from, 'ms');

        if (time <= INTERVAL_MINUTE) return INTERVAL_SECOND;
        if (time <= INTERVAL_TEN_MINUTES) return INTERVAL_15_SECONDS;
        if (time <= INTERVAL_THIRTY_MINUTES) return INTERVAL_MINUTE;
        if (time <= INTERVAL_ONE_HOUR) return INTERVAL_MINUTE;
        if (time <= INTERVAL_FOUR_HOURS) return INTERVAL_MINUTE;
        if (time <= INTERVAL_SIX_HOURS) return INTERVAL_FIFTEEN_MINUTES;
        if (time <= INTERVAL_TWELVE_HOURS) return INTERVAL_FIFTEEN_MINUTES;
        if (time <= INTERVAL_ONE_DAY) return INTERVAL_ONE_HOUR;
        if (time <= INTERVAL_ONE_WEEK) return INTERVAL_SIX_HOURS;
        return INTERVAL_ONE_DAY;
    },

    groupByInterval: (data: any[], interval: number): [number, number][] => {
        const { dateFormat } = Helper.getIntervalHandling(interval);
        const groupedData: Record<string, any[]> = {};
        const tz = getTimeZone();

        data.forEach((item: any) => {
            // Parse timeslice in user's selected timezone (API returns times in that timezone)
            const date = Helper.parseTimesliceWithTz(String(item.timeslice));
            const roundedTimestamp = Math.floor(date.valueOf() / interval) * interval; // Always rounds down
            // Format in user's timezone so key parses back correctly
            const roundedDate = dayjs.tz(roundedTimestamp, tz);
            const roundedDateString = roundedDate.format(dateFormat);

            if (!groupedData[roundedDateString]) {
                groupedData[roundedDateString] = [];
            }
            groupedData[roundedDateString].push(item);
        });

        // Convert grouped data into the desired output format. Parse key in user's timezone for correct timestamp.
        return Object.entries(groupedData).map(([key, items]) => [Helper.parseTimesliceWithTz(key).valueOf(), items.length]);
    },

    extractUniqueTimeslices: (alertsData: [number, number][], changesData: [number, number][], eventsData: [number, number][]) => {
        let uniqueTimeslices: any[] = [];

        function addUniqueTimeslices(data: any) {
            data.forEach((entry: string) => {
                if (!uniqueTimeslices.includes(entry[0])) {
                    uniqueTimeslices.push(entry[0]);
                }
            });
        }

        addUniqueTimeslices(alertsData);
        addUniqueTimeslices(changesData);
        addUniqueTimeslices(eventsData);

        uniqueTimeslices.sort((a, b) => a - b);

        return uniqueTimeslices;
    },

    populateMissingTimeslices: (data: [number, number][], uniqueTimeSlices: number[]) => {
        let timeSliceMap: any = {};
        data.forEach((entry: [number, number]) => {
            timeSliceMap[entry[0]] = entry[1];
        });

        uniqueTimeSlices.forEach(timeslice => {
            if (timeSliceMap[timeslice] === undefined) {
                data.push([timeslice, 0]);
            }
        });

        return data.sort((a, b) => a[0] - b[0]);
    },

    getTimeInEnglish(milliseconds: number) {
        if (!milliseconds) {
            return ''
        }
        milliseconds = parseFloat(milliseconds.toString())

        // if (milliseconds > 0 && milliseconds < 1) {
        //     return `${(milliseconds.toFixed(5))}ms`;
        // }
        let isNegative = milliseconds < 0;

        if (isNegative) {
            milliseconds = Math.abs(milliseconds);
        }

        if (milliseconds < 0) {
            return `${this.getFormattedNumber(milliseconds)}ms`;
        }

        if (milliseconds < 1000) {
            return `${milliseconds.toFixed(2)}ms`;
        }

        let description = '',
            seconds = milliseconds / 1000,
            days = Math.floor(seconds / (3600 * 24));

        seconds -= days * 3600 * 24;

        let hours = Math.floor(seconds / 3600);
        seconds -= hours * 3600;

        let minutes = Math.floor(seconds / 60);
        seconds -= minutes * 60;

        let ms = Math.floor(seconds / 60);
        ms -= seconds * 1000;

        if (days > 0) {
            description += days + 'd ';
        }

        if (hours > 0) {
            description += hours + 'h ';
        }

        if (minutes > 0) {
            description += minutes + 'm ';
        }

        if (seconds > 0) {

            if (Math.round(seconds) === 60) {

                // It's a minute.
                description = description.replace(minutes + 'm ', (minutes + 1) + 'm ');

                // if there is no description yet set i to 1 min
                if (description === '') {
                    description = '1m';
                }
            } else {

                // if there are only seconds, add 2 decimals
                if (hours < 1 && minutes < 1) {
                    description += seconds.toFixed(2) + 's ';
                } else {
                    // Keep the seconds.
                    description += Math.round(seconds) + 's ';
                }
            }
        }

        if (ms > 0) {
            description += Math.round(ms) + 'ms';
        }

        if (description === '') {
            description = '0ms';
        }

        return isNegative ? `-${description.trim()}` : description.trim();
    },

    getInstanceType(instance: InstanceTarget) {
        const config = INSTANCE_TYPE_CONFIG[instance.type];
        let instanceType = config?.displayName || 'Unknown';

        if (instance.version) {
            instanceType = `${instanceType} v${instance.version}`;
        }

        return instanceType;
    },

    getHostType(host: HostTarget) {

        let hostType = '';

        switch (host.type) {
            case HOST_TYPE_LINUX:
                hostType = 'Linux';
                break;
            case HOST_TYPE_WINDOWS:
                hostType = 'Windows';
                break;
            case HOST_TYPE_AWSRDS:
                hostType = 'AWS RDS';
                break;
            default:
                hostType = 'Unknown';
                break;
        }

        return hostType;
    },

    getPeakValue(data: (number | null)[]) {
        let values = data.map(function (value) {
            return value === null ? 0 : value;
        });

        return Math.max(...values);
    },

    getFormattedBadgeCount(count: number) {

        if (count > Number(process.env.REACT_APP_API_LIMIT) - 1) {
            return `${(Number(process.env.REACT_APP_API_LIMIT) - 1).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}+`
        }

        return count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    getFormattedNumber(count: number | null, metric: string = ''): string {
        if (count === null) {
            return '0'; // Return as a string instead of a number
        }
        if (metric === 'cpu_percent') {
            return count.toFixed(2).toString();
        }
        return Math.round(count)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    getFormattedPeriodHeadingFromDate(dateFrom: Date | Dayjs, dateTo: Date | Dayjs) {
        let from: Dayjs = dayjs.isDayjs(dateFrom) ? dateFrom : dayjs(dateFrom),
            to: Dayjs = dayjs.isDayjs(dateTo) ? dateTo : dayjs(dateTo);

        if (from.isSame(to, 'year')) {
            // Same year.
            if (from.isSame(to, 'month')) {
                // Same month.
                if (from.isSame(to, 'day')) {
                    // Same day.
                    return `${from.format('Do MMM HH:mm')} to ${to.format('HH:mm')}`;
                } else {
                    // Different day.
                    return `${from.format('Do HH:mm')} to ${to.format('Do MMM HH:mm')}`;
                }
            } else {
                // Different month.
                return `${from.format('Do MMM HH:mm')} to ${to.format('Do MMM HH:mm')}`;
            }
        } else {
            // Different year.
            return `${from.format('Do MMM YYYY HH:mm')} to ${to.format('Do MMM YYYY HH:mm')}`;
        }
    },

    getFormattedPeriodSubheading(from: Dayjs, to: Dayjs) {

        if (from.isSame(to, 'year')) {
            // Same year.
            if (from.isSame(to, 'month')) {
                // Same month.
                if (from.isSame(to, 'day')) {
                    // Same day.
                    return `${from.format('Do MMM')}`;
                } else {
                    // Different day.
                    return `${from.format('Do')} to ${to.format('Do MMM')}`;
                }
            } else {
                // Different month.
                return `${from.format('Do MMM')} to ${to.format('Do MMM')}`;
            }
        } else {
            // Different year.
            return `${from.format('Do MMM YYYY')} to ${to.format('Do MMM YYYY')}`;
        }
    },

    parseTimesliceWithTz(ts: string): Dayjs {
        const tz = getTimeZone();
        return dayjs.tz(ts, tz);
    },

    // Helper function to group items by timeslice
    groupItemsByTimeslice(items: CombinedChanges[], dateFormat: string): { [key: string]: CombinedChanges[] } {
        return items.reduce((acc, item) => {
            const ts = Helper.parseTimesliceWithTz(String(item.timeslice)).format(dateFormat);
            acc[ts] = acc[ts] || [];
            acc[ts].push(item);
            return acc;
        }, {} as { [key: string]: CombinedChanges[] });
    },

    getChartPlotLines(period: string | null, combinedChanges: CombinedChanges[], chartCategories: string[], interval: number, isReport: boolean = false, statementId?: string | undefined) {
        if (combinedChanges.length === 0) {
            return [];
        }

        const { dateFormat } = Helper.getIntervalHandling(interval);
        const updatedCombinedEvents = Helper.adjustTimesliceToClosestCategory(combinedChanges, chartCategories);

        // Filter events by type
        const changes = updatedCombinedEvents.filter(item => item.change);
        const events = updatedCombinedEvents.filter(item => item.custom);
        const plans = updatedCombinedEvents.filter(item => item.plan);
        const alerts = updatedCombinedEvents.filter(item => item.alert);

        // Group all items by timeslice using reduce (more efficient than forEach)
        const changesByTimeslice = Helper.groupItemsByTimeslice(changes, dateFormat);
        const eventsByTimeslice = Helper.groupItemsByTimeslice(events, dateFormat);
        const alertsByTimeslice = Helper.groupItemsByTimeslice(alerts, dateFormat);
        const plansByTimeslice = Helper.groupItemsByTimeslice(plans, dateFormat);

        // Get unique timeslices (using Set for better performance)
        const allEvents = [...changes, ...events, ...alerts, ...plans];
        const uniqueTimeslices = Array.from(
            new Set(allEvents.map(item => Helper.parseTimesliceWithTz(String(item.timeslice)).format(dateFormat)))
        ).sort();

        // Determine resource ID based on page context
        const isHostsPage = window.location.pathname.includes('/hosts/');
        const resourceId = isHostsPage ? combinedChanges[0]?.hostId : combinedChanges[0]?.instanceId;

        // Build plotlines
        const plotLines: ChartPlotLine[] = [];

        for (const timesliceStr of uniqueTimeslices) {
            const timeslice = Helper.parseTimesliceWithTz(timesliceStr);
            const formattedTimeslice = timeslice.format(dateFormat);

            // Get items for this timeslice
            const timesliceChanges = changesByTimeslice[formattedTimeslice] || [];
            const timesliceEvents = eventsByTimeslice[formattedTimeslice] || [];
            const timesliceAlerts = alertsByTimeslice[formattedTimeslice] || [];
            const timeslicePlans = plansByTimeslice[formattedTimeslice] || [];

            // Find chart position
            const chartIndex = chartCategories.indexOf(formattedTimeslice);

            // Build plotline if valid position found
            if (chartIndex > -1) {
                plotLines.push({
                    value: chartIndex,
                    timeValue: timeslice.valueOf(),
                    color: '#666',
                    width: 1,
                    dashStyle: 'dot',
                    label: {
                        useHTML: true,
                        align: 'left',
                        text: combinedIcons(
                            timesliceEvents,
                            timesliceChanges,
                            timesliceAlerts,
                            timeslicePlans,
                            period || '',
                            isReport,
                            resourceId,
                            statementId
                        ),
                        x: -8,
                        y: 15,
                        rotation: 0,
                        style: {
                            color: '#666',
                            fontWeight: 'normal'
                        }
                    },
                    zIndex: 1
                });
            }
        }

        return plotLines;
    },

    getChartPlotLinesForExecutionPlans(timeRange: any, executionPlans: ExecutionPlan[], chartCategories: string[]) {
        let plotLines: ChartPlotLine[] = [],
            dateFormat: string = '';

        switch (timeRange.interval) {
            case INTERVAL_SECOND:

                dateFormat = 'YYYY-MM-DD HH:mm:ss';
                break;

            default:

                dateFormat = 'YYYY-MM-DD HH:mm:00';
                break;
        }

        let uniqueTimeslices = executionPlans.map(plan => dayjs(plan.timeslice.toString().substring(0, 18)).format(dateFormat)).filter((x, i, a) => a.indexOf(x) === i);

        for (let index = 0; index < uniqueTimeslices.length; index++) {

            let timeslice = dayjs(uniqueTimeslices[index]);

            // Get total combined change count for the current timeslice.
            let timesliceChanges = executionPlans.filter(plan => timeslice.isSame(dayjs(plan.timeslice).format(dateFormat)));

            // Get the chart position.
            let value = chartCategories.findIndex(dateTime => dateTime === timeslice.format(dateFormat));

            // Build the plotlines.
            if (value > -1) {

                let totalPlans: string = timesliceChanges.length.toString();

                if (totalPlans === '1') {
                    totalPlans = '';
                }

                plotLines.push({
                    value,
                    timeValue: timeslice.valueOf(),
                    color: '#666',
                    width: 1,
                    dashStyle: 'dot',
                    label: {
                        useHTML: true,
                        text: `<i class="fal fa-ruler-triangle fa-fw mr-1"></i><sup class="text-secondary">${totalPlans}</sup>`,
                        x: -7,
                        y: 32,
                        rotation: 0,
                        style: {
                            color: '#666',
                            fontWeight: 'normal'
                        }
                    },
                    zIndex: 1
                });
            }
        }

        return plotLines;
    },

    getInstanaDatabaseIntegrations: async (instanaIntegrations: InstanaIntegration[], databases: Database[], instance: InstanceTarget) => {

        let serviceMatches: InstanaService[] = [];

        if (instanaIntegrations.length === 0 || databases.length === 0) {

            // No Instana integrations or databases defined, so exit method.
            return serviceMatches;
        }

        for (let index = 0; index < instanaIntegrations.length; index++) {

            // Get the current Instana integration.
            const
                instanaIntegration = instanaIntegrations[index],
                axiosRequestConfig = {
                    headers: {
                        'authorization': `apiToken ${instanaIntegration.apikey}`
                    }
                };

            // Loop through instance databases.
            for (let databaseIndex = 0; databaseIndex < databases.length; databaseIndex++) {

                // Get the current database name.
                const databaseName = databases[databaseIndex].database;

                if (databaseName && databaseName !== '-') {

                    try {
                        // Check Instana... with the name filter applied, we're only going to check one page for matches and take the first response data item.
                        const instanaServices: any = await axios.get(`${instanaIntegration.host}/api/application-monitoring/applications/services?page=1&nameFilter=${databaseName}`, axiosRequestConfig);

                        if (instanaServices.status !== 200) {

                            // Instana request was not successful.
                            console.error('Failed to retrieve a success response from Instana monitored services API.', instanaServices)

                            // Continue to next database instead of failing completely
                            continue;
                        }

                        // Make sure the items array exists.
                        instanaServices.data.items ||= [];

                        for (const item of instanaServices.data.items) {
                            if (item.types.includes('DATABASE') &&
                                item.technologies.includes
                                    (instanaTechnology(instance.type))) {
                                let instanaServiceId = item.id,
                                    instanaServiceTechnologies = item.technologies,
                                    instanaServiceUrl = `${instanaIntegration.host}#/service;serviceId=${item.id}/summary`;

                                serviceMatches.push({
                                    databaseName,
                                    instanaServiceId,
                                    instanaServiceTechnologies,
                                    instanaServiceUrl
                                });
                            }
                        }
                    } catch (error: any) {
                        // Show error popup but don't throw - Instana being unavailable shouldn't break the database tab
                        showError(
                            'Instana integration unavailable',
                            `Failed to fetch Instana services for database "${databaseName}": ${error?.message || error}`
                        );
                        // Continue processing other databases/integrations
                        continue;
                    }
                }
            }
        }

        return serviceMatches;
    },

    getQueryStringWithInterval(timeRangeContext: { timeRange: TimeRange }, from?: string, to?: string) {

        if (from === undefined) {
            from = timeRangeContext.timeRange.from.format(API_DATE_FORMAT)
        }

        if (to === undefined) {
            to = timeRangeContext.timeRange.to.format(API_DATE_FORMAT)
        }
        let interval = ''
        if (timeRangeContext.timeRange.interval) {
            interval = `&interval=${timeRangeContext.timeRange.interval}`
        }
        return `?fm=${from}&to=${to}&tz=${timeRangeContext.timeRange.timezone}${interval}`;
    },

    getPercentageDifference(firstNumber: number | null, secondNumber: number | null): null | number {

        if (firstNumber === null || firstNumber === 0) {

            // Percentages cannot be calculated when the first number is empty or zero.
            return null;
        }

        if (secondNumber === null) {

            secondNumber = 0;
        }

        const difference: number = (firstNumber - secondNumber);

        return Number(((difference / secondNumber) * 100).toFixed(0));
        // return Math.round((difference / secondNumber) * 100);
    },

    getAverageFromArray(array: number[]) {

        if (array.length === 0) {
            return 0;
        }

        const sum = array.reduce((a, b) => a + b, 0);

        return sum / array.length;
    },

    async createInstanceEvent(eventTypes: EventType[], id: number, eventTitle: string, eventDescription: string) {

        try {
            const title: string = 'DBmarlin Configuration Change';

            // Add a custom event type for monitored instance changes.
            let filteredEventTypes = eventTypes.filter((eventType: EventType) => eventType.title === title);

            if (filteredEventTypes.length === 0) {

                // Expected event type is missing, get the latest event types via another API call.
                let updatedEventTypes: any = await api.get('event/type');

                if (updatedEventTypes.status === 200) {

                    // Filter for the required event type (configuration change).
                    filteredEventTypes = updatedEventTypes.data.filter((eventType: EventType) => eventType.title === title);

                    if (filteredEventTypes.length === 0) {

                        // Expected event is missing, so create it.
                        await api.post('event/type', [{
                            eventTypeId: 0,
                            title,
                            htmlIconCode: 'fal fa-cog'
                        }]);

                        // Get the event types again - the APIs do not return the just created object ID.
                        updatedEventTypes = await api.get('event/type');
                    }

                    if (updatedEventTypes.status === 200) {

                        filteredEventTypes = updatedEventTypes.data.filter((eventType: EventType) => eventType.title === title);
                    }

                    /*
                    // Todo: convert this component to a function, and get the event types context updated (there is not enough time right now).
                    // Sort the array order and add the new object to the context.
                    setEventTypes(updatedEventTypes.concat(data).sort(function (a: EventType, b: EventType) {
                        if (a.title < b.title) { return -1; }
                        if (a.title > b.title) { return 1; }
                        return 0;
                    }));
                    */
                }
            }

            if (filteredEventTypes.length > 0) {

                // Create event for creation of database instance monitoring.
                await api.post('event', [{
                    eventId: 0,
                    databaseTargetId: id,
                    eventTypeId: filteredEventTypes[0].eventTypeId,
                    startDateTime: new Date(),
                    endDateTime: null,
                    colourCode: '#6c757d',
                    title: eventTitle,
                    description: eventDescription,
                    detailsUrl: ''
                }]);

            } else {

                // Not a critical error as such, so just add a warning to the console.
                console.warn('"DBmarlin Configuration Change" event type missing - unable to create event type and event for the requested instance action');
            }
        } catch (error: any) {
            console.error('Failed to create instance event.', error);
            showError('Failed to create instance event', error.message || error.toString());
        }
    },

    updateFilterOptions(filterType: FilterType, filter: string, existingOptions: FilterOption[]): FilterOption[] {

        if (filter === '-') {
            return existingOptions;
        }

        let options: FilterOption[] = existingOptions;

        let filteredOption: FilterOption[] = options.filter(option => option.filterType === filterType);

        if (filteredOption.length > 0 &&
            !filteredOption[0].filters.includes(filter)) {

            // Add the new filter option.
            filteredOption[0].filters.push(filter);

            const updatedFilterOptions = options.map(option =>
                option.filterType === filterType
                    ? { ...option, filters: filteredOption[0].filters }
                    : option
            );

            // Return the updated filters.
            return updatedFilterOptions;
        }

        return existingOptions;
    },

    getChangeIcon(val1: number, val2: number) {
        if (!val1 && val2) {
            return 'fas fa-long-arrow-down changed-up';
        }

        if (val1 && !val2) {
            return 'fas fa-long-arrow-down changed-down';
        }


        if (val1 === 0 && val2 === 0) {
            return "fas fa-equals no-change"
        }

        if (!val1 || !val2) {
            return 'no-comparison';
        }

        if (val1 === val2) {
            return "fas fa-equals no-change"
        }

        return val1 < val2 ? "fas fa-long-arrow-down changed-up" : "fas fa-long-arrow-down changed-down"
    },

    getNumberFormatType(metric: string, value: number) {
        if ((TIME_METRICS.includes(metric) || metric.includes('_milliseconds')) && (metric !== 'cpu_time')) {
            return Helper.getTimeInEnglish(value)
        }
        if (metric.includes('_percent')) {
            return `${parseFloat(value.toString()).toFixed(2)}%`
        }
        return Helper.getFormattedNumber(value)
    },

    getMetricFormatString(metric: string, useParentheses = false) {
        if ((TIME_METRICS.includes(metric) || metric.includes('_milliseconds')) && (metric !== 'cpu_time')) {
            return useParentheses ? '(ms)' : 'ms'
        }
        if (metric.includes('_percent')) {
            return useParentheses ? '(%)' : '%'
        }
        return ''
    },

    getToolTip(val1: number, val2: number, metric: string) {
        if ((val1 === val2) || (!val1 || !val2)) {
            return null
        }
        const value = val2 - val1
        const isTime = ((TIME_METRICS.includes(metric) || metric.includes('_milliseconds')) && (metric !== 'cpu_time'))
        const sign = value > 0 ? '+' : isTime ? '-' : ''
        return isTime ? `${sign}${Helper.getTimeInEnglish(Math.abs(value))}` : `${sign}${Helper.getFormattedNumber(value)}`
    },

    addAverageAndColor(time: string, count: string, list: any[], toAddColor: number) {
        const listWithAverage: any[] = []

        list.forEach((item, index) => {
            const noZeroCountValue = item[count] || 1
            item.average_duration = item[time] / noZeroCountValue
            item.color = index < toAddColor ? TARGET_COLOUR_CODES[index] : 'transparent'
            listWithAverage.push(item)
        })
        return listWithAverage
    },

    isComparisonSupported(period: { timeRange: TimeRange }) {
        const timePeriod = period.timeRange
        if (timePeriod) {
            const diff = timePeriod.to.diff(timePeriod.from, 'ms');
            const previousFromDiff = timePeriod.previousFrom.isBefore(dayjs().subtract(31, 'day'));
            // Over week period
            if (diff > 604800000 || previousFromDiff) {
                return false
            }
        }

        return true
    },

    aggregateIsValid(aggregateValue: number, timeRangeContext: { timeRange: TimeRange }) {
        // When URL has fm/to/tz (e.g. recommendation link), preserve the interval from the link - don't adjust to period selector
        const urlHasTimeParams = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('fm') && new URLSearchParams(window.location.search).get('to');
        if (urlHasTimeParams) return true;

        const differenceInMilliseconds = timeRangeContext.timeRange.to.diff(timeRangeContext.timeRange.from, 'milliseconds');
        const getTimePeriod = determineTimeUnit(differenceInMilliseconds)
        const isValid = periodToValidIntervals[getTimePeriod].includes(aggregateValue)
        if (!isValid) {
            window.localStorage.setItem('dbmar-interval', '0')
        }
        return isValid
    },

    filterTagsByTagName(tags: any[]) {
        const uniqueTags: any = {};


        tags?.forEach(item => {
            if (!uniqueTags[item.tagname]) {
                uniqueTags[item.tagname] = [item.tagvalue];
            } else {
                if (!uniqueTags[item.tagname].includes(item.tagvalue)) {
                    uniqueTags[item.tagname].push(item.tagvalue);
                }
            }
        });
        const result: { tagname: string; tagvalues: string[] }[] = Object.keys(uniqueTags).map(tagname => ({
            tagname,
            tagvalues: uniqueTags[tagname]
        }));
        return result

    },

    filterArraysByKey(a: any[], b: any[], key: string) {
        const namesInB = new Set(b.map(item => item[key]));
        return a.filter(item => !namesInB.has(item[key]));
    },

    checkForTwoWithSameKeyValue(key: string, data: any[]) {
        const nameCount: any = {};
        let isDuplicate = false
        for (const item of data) {
            if (nameCount[item[key]]) {
                nameCount[item[key]]++;
                if (nameCount[item[key]] === 2) {
                    isDuplicate = true
                }
            } else {
                nameCount[item[key]] = 1;
            }
        }
        return isDuplicate
    },

    getUrlParamsForTags(tags: any[]) {
        const tagsParam = tags.map(tag => `${tag.tagName}:${tag.tagValues.join(',')}`).join(';');
        return tagsParam
    },

    fillChartDataWithZeroValues(data: { [key: string]: { values: any[], orderIndex: number } }, metric: string) {

        // Find the complete set of timeslices
        const allTimeslices = new Set();
        for (const key in data) {
            if (Object.hasOwnProperty.call(data, key)) {
                const innerObj = data[key];
                if (innerObj.values && Array.isArray(innerObj.values)) {
                    innerObj.values.forEach((dataPoint: { timeslice: unknown; }) => {
                        allTimeslices.add(dataPoint.timeslice);
                    });
                }
            }
        }
        // sort timeslices
        const allTimeslicesArray = Array.from(allTimeslices).sort();

        // Fill in missing timeslices with a value of 0 for each object
        for (const key in data) {
            if (Object.hasOwnProperty.call(data, key)) {
                const innerObj: any = data[key];
                if (innerObj.values && Array.isArray(innerObj.values)) {
                    const timesliceSet: any = new Set(innerObj.values.map((dataPoint: {
                        timeslice: any;
                    }) => dataPoint.timeslice));
                    allTimeslicesArray.forEach(timeslice => {
                        if (!timesliceSet.has(timeslice)) {
                            innerObj.values.push({ timeslice: timeslice, [metric]: 0 });
                        }
                    });
                    innerObj.values.sort((a: any, b: any) => (a.timeslice > b.timeslice ? 1 : -1)); // Sort by timeslice
                }
            }
        }
        return data
    },

    showInterimChanges(options: InstanceOptions[]): boolean {
        const periodEnd0 = new Date(options[0].periodEnd);
        const periodStart1 = new Date(options[1].periodStart);

        const differenceInMillis = periodStart1.getTime() - periodEnd0.getTime();
        const differenceInDays = differenceInMillis / (1000 * 60 * 60 * 24); // Convert milliseconds to days

        return options[0].periodEnd < options[1].periodStart && differenceInDays < 7;
    },

    getIntervalHandling(interval: number) {
        let dateFormat: string;
        // let incrementUnit: number;

        switch (interval) {
            case INTERVAL_SECOND:
            case INTERVAL_5_SECONDS:
            case INTERVAL_10_SECONDS:
            case INTERVAL_15_SECONDS:
                dateFormat = 'YYYY-MM-DD HH:mm:ss';
                // incrementUnit = INTERVAL_SECOND;
                break;
            case INTERVAL_MINUTE:
                dateFormat = 'YYYY-MM-DD HH:mm:00';
                // incrementUnit = INTERVAL_MINUTE;
                break;
            case INTERVAL_FIVE_MINUTES:
            case INTERVAL_TEN_MINUTES:
                dateFormat = 'YYYY-MM-DD HH:mm:00';
                // incrementUnit = INTERVAL_FIVE_MINUTES;
                break;
            case INTERVAL_FIFTEEN_MINUTES:
            case INTERVAL_THIRTY_MINUTES:
                dateFormat = 'YYYY-MM-DD HH:mm:00';
                // incrementUnit = INTERVAL_FIVE_MINUTES;
                break;
            case INTERVAL_ONE_HOUR:
            case INTERVAL_FOUR_HOURS:
                dateFormat = 'YYYY-MM-DD HH:mm:00';
                // incrementUnit = INTERVAL_FIFTEEN_MINUTES;
                break;
            case INTERVAL_SIX_HOURS:
            case INTERVAL_TWELVE_HOURS:
                dateFormat = 'YYYY-MM-DD HH:mm:00';
                // incrementUnit = INTERVAL_ONE_HOUR;
                break;
            case INTERVAL_ONE_DAY:
            case INTERVAL_ONE_WEEK:
                dateFormat = 'YYYY-MM-DD HH:00:00';
                // incrementUnit = INTERVAL_ONE_DAY;
                break;
            default:
                dateFormat = 'YYYY-MM-DD HH:mm:00';
                // incrementUnit = INTERVAL_MINUTE
                break;
        }

        return { dateFormat };
    },

    adjustTimesliceToClosestCategory(events: any[], categories: string[]) {
        const parsedCategories = categories.map((category) => Helper.parseTimesliceWithTz(category));

        return events.map((event) => {
            const eventTime = Helper.parseTimesliceWithTz(String(event.timeslice));

            // Find the previous category (less than or equal to eventTime)
            let previousCategory = parsedCategories[0];

            for (const category of parsedCategories) {
                if (category.isBefore(eventTime) || category.isSame(eventTime)) {
                    previousCategory = category;
                } else {
                    // Stop searching once a category is greater than eventTime
                    break;
                }
            }

            // Update the timeslice to the previous category
            return { ...event, timeslice: previousCategory.format('YYYY-MM-DD HH:mm:ss') };
        });
    },
    formatPercentage(value: any) {
        if (value === 100) return '100';
        if (value > 0 && value < 0.01) return '0.01';
        if (value < 1) return value.toFixed(2);
        return String(Math.round(value));
    }
};

export default Helper;
